/**
 * Billing cadence → monthly run-rate.
 *
 * A recurring line is billed on SOME rhythm, and only one of those rhythms is
 * already a monthly number. Summing a $12,000 annual charge next to a $500
 * monthly one and calling the total "MRR" overstates the month by $11,500 —
 * which is exactly what `dashboard_recurring_charges_summary` does today
 * (`sum(metadata->>'amount')`, cadence-blind, see the README).
 *
 * So: one vocabulary, one conversion table, one function. Every surface that
 * wants "what is this worth per month" calls `normalizeToMrr`.
 *
 * ## Why months-per-period and not days
 *
 * A month is not 30 days and a year is not 360, so a day-based conversion has
 * to pick a fudge factor and then defend it. MRR is a *month* measure: the
 * honest conversion is "how many months does one period cover", which is exact
 * for monthly/quarterly/annual and a documented convention for the two
 * sub-monthly cadences (see `MONTHS_PER_PERIOD`).
 *
 * ## `one_time` is deliberately in the vocabulary and worth 0
 *
 * A one-off charge is a real thing a recurring-billing table holds, and the
 * dangerous outcome is not "we forgot to model it" — it is someone reading a
 * one-off $30,000 implementation fee as $30,000 of MRR. Naming it, and
 * returning 0, makes the exclusion a decision rather than an omission.
 */

/**
 * Every cadence WTC bills on.
 *
 * `daily` / `weekly` / `monthly` / `annual` are the four `recurring_charges.schedule_type`
 * values the charge scheduler already writes (see `RECURRING_CHARGE_CADENCES`
 * below). `quarterly` and `one_time` are not in that column's vocabulary today
 * but are in this one because the conversion question is asked of deal and
 * invoice data too, where both occur.
 */
export const RECURRING_CADENCES = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annual",
  "one_time",
] as const;

export type RecurringCadence = (typeof RECURRING_CADENCES)[number];

/**
 * The subset the `recurring_charges.schedule_type` column actually stores —
 * the charge scheduler and the Meridian recurring-charge tools both validate
 * against exactly these.
 *
 * Kept as its own list rather than "the cadences minus two" so that adding a
 * cadence to the wider vocabulary does not silently teach the scheduler a
 * rhythm it has no `calculateNextChargeDate` arm for.
 */
export const RECURRING_CHARGE_CADENCES = [
  "daily",
  "weekly",
  "monthly",
  "annual",
] as const;

export type RecurringChargeCadence = (typeof RECURRING_CHARGE_CADENCES)[number];

/**
 * How many months one billing period covers.
 *
 * The two sub-monthly numbers are conventions and are written here once so they
 * cannot be re-guessed per call site:
 *   - `weekly`  — 52 weeks / 12 months = 4.333… billings per month.
 *   - `daily`   — 365 days / 12 months = 30.4166… billings per month.
 * Both use the 365-day year rather than 365.25: a run-rate is a forward-looking
 * statement about a normal year, and no downstream number is precise enough for
 * a leap day to matter.
 */
export const MONTHS_PER_PERIOD: Readonly<Record<RecurringCadence, number>> = {
  daily: 12 / 365,
  weekly: 12 / 52,
  monthly: 1,
  quarterly: 3,
  annual: 12,
  /** A one-off is not a run-rate. See the header. */
  one_time: Number.POSITIVE_INFINITY,
};

const cadenceSet: ReadonlySet<string> = new Set(RECURRING_CADENCES);

/**
 * Aliases the databases and humans actually write, mapped onto the vocabulary.
 *
 * Deliberately a fixed table rather than fuzzy matching: a cadence this does not
 * recognise must come back `null` so the caller can refuse the row, not be
 * quietly bucketed as monthly (which is the failure that overstates a book).
 */
const CADENCE_ALIASES: Readonly<Record<string, RecurringCadence>> = {
  day: "daily",
  daily: "daily",
  week: "weekly",
  weekly: "weekly",
  month: "monthly",
  monthly: "monthly",
  mo: "monthly",
  quarter: "quarterly",
  quarterly: "quarterly",
  qtr: "quarterly",
  year: "annual",
  yearly: "annual",
  annual: "annual",
  annually: "annual",
  once: "one_time",
  onetime: "one_time",
  one_time: "one_time",
  "one-time": "one_time",
  onceoff: "one_time",
};

/**
 * Parse a stored/typed cadence string. Unknown input → `null`, never a default.
 *
 * A default here would be a silent mis-statement of revenue: the caller is the
 * only place that knows whether an unrecognised `schedule_type` should be
 * dropped, flagged, or made to fail the request.
 */
export function parseCadence(raw: string | null | undefined): RecurringCadence | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase().replace(/\s+/g, "_");
  if (cadenceSet.has(key)) return key as RecurringCadence;
  return CADENCE_ALIASES[key] ?? CADENCE_ALIASES[key.replace(/_/g, "")] ?? null;
}

/** Is this one of the four values `recurring_charges.schedule_type` may hold? */
export function isRecurringChargeCadence(
  value: string | null | undefined
): value is RecurringChargeCadence {
  const parsed = parseCadence(value);
  return parsed !== null && (RECURRING_CHARGE_CADENCES as readonly string[]).includes(parsed);
}

const numeric = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The monthly-equivalent value of `amount` billed on `cadence`.
 *
 * Returns `0` for a one-off and for anything that isn't a finite number —
 * NEVER `NaN`, which propagates through a `reduce` and turns a whole dashboard
 * tile into "$NaN" with no clue which row did it.
 *
 * Rounded to cents, because every consumer either displays this or sums it and
 * then displays it, and `4.333…` weekly periods otherwise leak float noise into
 * a currency string.
 */
export function normalizeToMrr(
  amount: number | string | null | undefined,
  cadence: RecurringCadence
): number {
  const value = numeric(amount);
  if (value === null) return 0;
  const months = MONTHS_PER_PERIOD[cadence];
  if (!Number.isFinite(months) || months <= 0) return 0;
  return roundCents(value / months);
}

/**
 * Same, from an unparsed cadence string. Unrecognised cadence → `null` so the
 * caller must decide what an unbillable row means; a recognised one → the
 * monthly-equivalent.
 */
export function normalizeToMrrFromRaw(
  amount: number | string | null | undefined,
  rawCadence: string | null | undefined
): number | null {
  const cadence = parseCadence(rawCadence);
  if (cadence === null) return null;
  return normalizeToMrr(amount, cadence);
}

/** Cents-precision rounding, shared so movement and cadence agree to the penny. */
export function roundCents(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

/** Human label for a cadence — the phone renders this verbatim. */
export const CADENCE_LABELS: Readonly<Record<RecurringCadence, string>> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
  one_time: "One-time",
};
